import { useTranslation } from 'react-i18next';
import { Button, Modal, ModalActions, ModalContent, ModalTitleInfo } from '@c2fo/liquidity';
import trashCan from '@/enterprise/assets/trashcan.svg?url';
import { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import getTakerMarketDivisionTitle from '@/utils/getTakerMarketDivisionTitle';
import getTakerMarketName from '@/utils/getTakerMarketName';
import useLocaleFormat from '@/utils/useLocaleFormat';

export interface RemoveZeroDivisionsModalProps {
  open: boolean;
  zeroDivisions: TakerMarket[];
  onClose: () => void;
  onConfirm: () => void;
}

const RemoveZeroDivisionsModal = ({ open, zeroDivisions, onClose, onConfirm }: RemoveZeroDivisionsModalProps) => {
  const { t } = useTranslation();
  const { asNumber } = useLocaleFormat();

  const handleConfirm = () => {
    onConfirm();
    onClose();
  };

  const zeroDivisionsCount = asNumber({ value: zeroDivisions.length });
  const modalTitle = t('builder.removeZeroDivisionsModal.title', {
    count: zeroDivisions.length,
    formattedCount: zeroDivisionsCount,
  });

  return (
    <Modal open={open} onClose={onClose} size="sm">
      <ModalTitleInfo>{modalTitle}</ModalTitleInfo>
      <ModalContent className="space-y-6">
        <div className="flex justify-center">
          <img src={trashCan} alt="trash can" className="size-20" />
        </div>
        <div className="space-y-4">
          <div>{t('builder.removeZeroDivisionsModal.explanation')}</div>
          <ul className="max-h-60 space-y-3 overflow-y-auto rounded-md border p-4">
            {zeroDivisions.map((division) => (
              <li key={division.id} className="flex list-none gap-3 border-b pb-3 last:border-0 last:pb-0">
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium">{getTakerMarketName(division)}</div>
                  <div className="truncate text-sm font-medium" title={getTakerMarketDivisionTitle(division)?.title}>
                    {getTakerMarketDivisionTitle(division)?.content}
                  </div>
                  <div className="text-sm text-text-secondary">{division.currency}</div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </ModalContent>
      <ModalActions>
        <Button onClick={onClose} variant="secondary">
          {t('builder.removeZeroDivisionsModal.goBack')}
        </Button>
        <Button onClick={handleConfirm} variant="primary">
          {t('builder.removeZeroDivisionsModal.removeAndContinue')}
        </Button>
      </ModalActions>
    </Modal>
  );
};

export default RemoveZeroDivisionsModal;
